/*
  CYRIL video player — one player core for the whole theme, shown two ways:
  - inline: [data-cyril-video][data-cyril-video-inline] plays inside the closest [data-cvp-inline-host]
    as a seamless cover-fit film with only an exit button (home scenes);
  - modal: any other [data-cyril-video] opens the shared <cyril-video-modal>.
  Sources: data-cyril-video='[{"src","type","label"}]' (see cyril-video-trigger.liquid).
  Video.js 8.24.1 loads on first play; Auto (HLS) plus manual MP4 qualities when available;
  centred circle while loading; retry on failure; no captions or picture-in-picture.
*/
(() => {
  if (window.CyrilPlayer) return;

  const loadLibrary = (scriptUrl, styleUrl) => {
    if (window.cyrilVideoLibraryPromise) return window.cyrilVideoLibraryPromise;
    const load = (type, url) => new Promise((resolve, reject) => {
      const element = document.createElement(type);
      const timer = setTimeout(() => finish(new Error('Player loading timed out')), 20000);
      const finish = (error) => {
        clearTimeout(timer);
        element.onload = element.onerror = null;
        if (error) {
          element.remove();
          reject(error);
        } else {
          resolve();
        }
      };
      element.onload = () => finish();
      element.onerror = () => finish(new Error('Player asset unavailable'));
      if (type === 'script') {
        element.src = url;
        element.async = true;
      } else {
        element.rel = 'stylesheet';
        element.href = url;
      }
      document.head.append(element);
    });
    window.cyrilVideoLibraryPromise = Promise.all([
      window.videojs ? Promise.resolve() : load('script', scriptUrl),
      load('link', styleUrl),
    ]).catch((error) => {
      window.cyrilVideoLibraryPromise = null;
      throw error;
    });
    return window.cyrilVideoLibraryPromise;
  };

  // Player core: mounts Video.js into `frame` (which holds [data-cvp-mount], [data-cvp-status],
  // [data-cvp-failure] and [data-cvp-retry]) and returns a handle with play/pause/dispose.
  class CyrilPlayer {
    constructor(frame, options) {
      this.frame = frame;
      this.options = options;
      this.mount = frame.querySelector('[data-cvp-mount]');
      this.status = frame.querySelector('[data-cvp-status]');
      this.failure = frame.querySelector('[data-cvp-failure]');
      this.request = 0;
      frame.querySelector('[data-cvp-retry]').onclick = () => this.play(this.sources);
    }

    dispose() {
      this.request += 1;
      if (this.player && !this.player.isDisposed()) this.player.dispose();
      this.player = null;
      this.mount.replaceChildren();
      this.status.hidden = true;
      this.failure.hidden = true;
      this.frame.style.removeProperty('--cvp-ar');
    }

    pause() {
      if (this.player && !this.player.isDisposed()) this.player.pause();
    }

    async play(sources) {
      this.dispose();
      this.sources = sources;
      const request = this.request;
      const { scriptUrl, styleUrl, qualityLabel } = this.options;
      this.status.hidden = false;
      try {
        await loadLibrary(scriptUrl, styleUrl);
      } catch (error) {
        if (request !== this.request) return;
        this.status.hidden = true;
        this.failure.hidden = false;
        return;
      }
      if (request !== this.request) return;

      const video = document.createElement('video');
      video.className = 'video-js vjs-big-play-centered';
      video.setAttribute('playsinline', '');
      this.mount.append(video);

      let selected = 0;
      const player = window.videojs(video, {
        sources: [{ src: sources[0].src, type: sources[0].type }],
        controls: true,
        autoplay: false,
        preload: 'auto',
        playsinline: true,
        enableSmoothSeeking: true,
        disableSeekWhileScrubbingOnMobile: true,
        html5: { vhs: { overrideNative: false } },
        controlBar: {
          pictureInPictureToggle: false,
          subsCapsButton: false,
          captionsButton: false,
          subtitlesButton: false,
          descriptionsButton: false,
          volumePanel: { inline: false },
        },
      });
      this.player = player;

      let switching = false;
      const loading = () => { if (this.failure.hidden) this.status.hidden = false; };
      const settled = () => { if (!switching) this.status.hidden = true; };
      player.on(['loadstart', 'waiting', 'seeking'], loading);
      player.on(['playing', 'seeked', 'ended', 'pause'], settled);
      player.on('canplay', () => { if (player.paused()) settled(); });
      player.on('error', () => { this.status.hidden = true; this.failure.hidden = false; });
      player.on('loadedmetadata', () => {
        const width = player.videoWidth();
        const height = player.videoHeight();
        if (width && height) this.frame.style.setProperty('--cvp-ar', String(width / height));
      });

      // Started by a click, so try with sound first; fall back to muted if the browser refuses.
      const start = () => {
        player.play()?.catch(() => {
          if (player.isDisposed()) return;
          player.muted(true);
          player.play()?.catch(() => {
            if (player.isDisposed()) return;
            this.status.hidden = true;
            player.hasStarted(false);
          });
        });
      };

      const choose = (index) => {
        if (index === selected) return;
        const resumeTime = player.currentTime();
        const resume = !player.paused();
        selected = index;
        switching = true;
        this.failure.hidden = true;
        this.status.hidden = false;
        player.one('loadedmetadata', () => {
          if (player.isDisposed()) return;
          switching = false;
          if (resumeTime > 0) player.currentTime(Math.min(resumeTime, Math.max(0, player.duration() - 0.1)));
          if (resume) start();
          else this.status.hidden = true;
        });
        player.src({ src: sources[index].src, type: sources[index].type });
        if (resume) start();
      };

      if (sources.length > 1) addQualityMenu(player, sources, () => selected, choose, qualityLabel);
      player.ready(start);
    }
  }

  function addQualityMenu(player, sources, current, choose, label) {
    const MenuButton = window.videojs.getComponent('MenuButton');
    const MenuItem = window.videojs.getComponent('MenuItem');
    class QualityItem extends MenuItem {
      handleClick(event) {
        super.handleClick(event);
        this.options_.pick();
      }
    }
    class QualityMenu extends MenuButton {
      constructor(owner, options) {
        super(owner, options);
        this.addClass('cvp-quality');
        this.controlText(label);
        this.menuButton_.el().querySelector('.vjs-icon-placeholder').classList.add('vjs-icon-cog');
      }

      createItems() {
        return sources.map((source, index) => new QualityItem(this.player_, {
          label: source.label || `${index + 1}`,
          selectable: true,
          selected: index === current(),
          pick: () => {
            choose(index);
            this.items.forEach((item, i) => item.selected(i === current()));
            this.unpressButton();
          },
        }));
      }
    }
    const menu = new QualityMenu(player, { title: label });
    const fullscreenIndex = player.controlBar.children().indexOf(player.controlBar.getChild('fullscreenToggle'));
    player.controlBar.addChild(menu, {}, fullscreenIndex);
  }

  const readSources = (trigger) => {
    try {
      const sources = JSON.parse(trigger.dataset.cyrilVideo);
      const auto = trigger.dataset.autoLabel || 'Auto';
      return sources.map((source) => ({ ...source, label: source.label === 'auto' ? auto : source.label }));
    } catch (error) {
      return [];
    }
  };

  const optionsFrom = (el) => ({
    scriptUrl: el.dataset.playerScript,
    styleUrl: el.dataset.playerStyle,
    qualityLabel: el.dataset.qualityLabel || 'Quality',
  });

  const frameMarkup = (el) => `
    <div class="cvp__mount" data-cvp-mount></div>
    <div class="cvp__status" data-cvp-status role="status" hidden><span class="cvp__spinner" aria-hidden="true"></span></div>
    <div class="cvp__failure" data-cvp-failure role="alert" hidden>
      <p>${el.dataset.errorLabel || 'Unable to load this video.'}</p>
      <button type="button" data-cvp-retry>${el.dataset.retryLabel || 'Retry'}</button>
    </div>`;

  // Inline: the video blends into its host (e.g. a scene stage) like a background film — cover-fit,
  // no player chrome; only a small quality switch and an exit button. Ends → restores the host.
  // Shopify videos play their adaptive stream through Video.js with controls off (Auto + manual
  // heights from the stream); a plain MP4 link plays in a native <video>.
  const inline = {
    host: null,
    async open(trigger, host) {
      if (this.host === host) return;
      this.close();
      const sources = readSources(trigger);
      if (!sources.length) return;
      const stream = sources.find((source) => source.type === 'application/x-mpegURL');
      const file = sources.find((source) => source.type !== 'application/x-mpegURL');
      const autoLabel = trigger.dataset.autoLabel || 'Auto';
      this.host = host;
      this.trigger = trigger;
      const request = (this.request = (this.request || 0) + 1);

      const layer = document.createElement('div');
      layer.className = 'cvp cvp--inline';
      layer.innerHTML = `
        <div class="cvp__film" data-cvp-film></div>
        <div class="cvp__status" data-cvp-status role="status"><span class="cvp__spinner" aria-hidden="true"></span></div>
        <div class="cvp__tools">
          <div class="cvp__volume">
            <button type="button" class="cvp__icon-button" data-cvp-mute aria-pressed="false" aria-label="Mute"><svg class="cvp__icon-sound" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg><svg class="cvp__icon-muted" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M16 10l4 4M20 10l-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
            <input type="range" class="cvp__volume-range" data-cvp-volume min="0" max="1" step="0.05" value="1" aria-label="Volume">
          </div>
          <div class="cvp__quality" hidden>
            <button type="button" class="cvp__icon-button cvp__quality-toggle" data-cvp-quality aria-haspopup="true" aria-expanded="false" aria-label="${trigger.dataset.qualityLabel || 'Quality'}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M19.4 13.5a7.6 7.6 0 0 0 0-3l2-1.6-2-3.4-2.4 1a7.4 7.4 0 0 0-2.6-1.5L14 2.5h-4l-.4 2.5A7.4 7.4 0 0 0 7 6.5l-2.4-1-2 3.4 2 1.6a7.6 7.6 0 0 0 0 3l-2 1.6 2 3.4 2.4-1A7.4 7.4 0 0 0 9.6 19l.4 2.5h4l.4-2.5a7.4 7.4 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.6Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg></button>
            <ul class="cvp__quality-menu" role="menu" hidden></ul>
          </div>
          <button type="button" class="cvp__icon-button" data-cvp-fullscreen aria-label="${trigger.dataset.fullscreenLabel || 'Full screen'}"><svg class="cvp__icon-enter" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg><svg class="cvp__icon-exit" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></button>
          <button type="button" class="cvp__close" data-cvp-inline-close aria-label="${trigger.dataset.closeLabel || 'Close'}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
        </div>`;
      host.append(layer);
      this.layer = layer;
      const status = layer.querySelector('[data-cvp-status]');
      layer.querySelector('[data-cvp-inline-close]').addEventListener('click', () => this.close());
      layer.querySelector('[data-cvp-fullscreen]').addEventListener('click', () => this.toggleFullscreen());
      host.classList.add('is-playing-video');
      host.dispatchEvent(new CustomEvent('cyril:video-open', { bubbles: true }));
      requestAnimationFrame(() => layer.classList.add('is-open'));
      layer.querySelector('[data-cvp-inline-close]').focus({ preventScroll: true });

      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.preload = 'auto';
      layer.querySelector('[data-cvp-film]').append(video);
      let media = video;

      if (stream) {
        // Without the library, fall back to the plain MP4 (or the stream natively on Safari).
        await loadLibrary(trigger.dataset.playerScript, trigger.dataset.playerStyle).catch(() => {});
        if (request !== this.request) return;
      }

      if (stream && window.videojs) {
        video.className = 'video-js';
        this.player = window.videojs(video, {
          controls: false,
          autoplay: false,
          preload: 'auto',
          playsinline: true,
          loadingSpinner: false,
          bigPlayButton: false,
          errorDisplay: false,
          html5: { vhs: { overrideNative: true } },
          sources: [{ src: stream.src, type: stream.type }],
        });
        media = this.player;
        this.setupQuality(layer, this.player, autoLabel);
      } else {
        video.src = (file || stream).src;
      }

      const on = (name, fn) => (media === video ? video.addEventListener(name, fn) : media.on(name, fn));
      this.setupVolume(layer, media === video ? video : {
        get muted() { return media.muted(); },
        set muted(value) { media.muted(value); },
        get volume() { return media.volume(); },
        set volume(value) { media.volume(value); },
      }, on);
      on('playing', () => { layer.classList.add('is-playing'); status.hidden = true; });
      on('waiting', () => { status.hidden = false; });
      on('ended', () => this.close());
      on('error', () => this.close());

      const paused = () => (media === video ? video.paused : media.paused());
      this.observer = new IntersectionObserver(([entry]) => {
        if (document.fullscreenElement === layer || document.webkitFullscreenElement === layer) return;
        if (!entry.isIntersecting) media.pause();
        else if (paused() && layer.classList.contains('is-playing')) Promise.resolve(media.play()).catch(() => {});
      }, { threshold: 0.25 });
      this.observer.observe(host);

      // Started by a click, so try with sound first; fall back to muted if the browser refuses.
      const play = () => media.play();
      const mute = () => (media === video ? (video.muted = true) : media.muted(true));
      Promise.resolve(play()).catch(() => {
        if (request !== this.request) return;
        mute();
        Promise.resolve(play()).catch(() => this.close());
      });
    },

    // Sound: the button mutes/unmutes everywhere; the slider is for mouse users (iPhone volume is hardware-only).
    setupVolume(layer, media, on) {
      const button = layer.querySelector('[data-cvp-mute]');
      const range = layer.querySelector('[data-cvp-volume]');
      const render = () => {
        const silent = media.muted || media.volume === 0;
        layer.classList.toggle('is-muted', silent);
        button.setAttribute('aria-pressed', String(silent));
        button.setAttribute('aria-label', silent ? 'Unmute' : 'Mute');
        range.value = silent ? 0 : media.volume;
        range.style.setProperty('--cvp-volume', `${(silent ? 0 : media.volume) * 100}%`);
      };
      button.addEventListener('click', () => {
        const silent = media.muted || media.volume === 0;
        if (silent && media.volume === 0) media.volume = 1;
        media.muted = !silent;
      });
      range.addEventListener('input', () => {
        const value = Number(range.value);
        media.volume = value;
        media.muted = value === 0;
      });
      on('volumechange', render);
      render();
    },

    // Quality from the adaptive stream: Auto enables every rendition; a height pins one.
    setupQuality(layer, player, autoLabel) {
      const wrap = layer.querySelector('.cvp__quality');
      const toggle = layer.querySelector('[data-cvp-quality]');
      const menu = layer.querySelector('.cvp__quality-menu');
      const levels = player.qualityLevels?.();
      if (!levels) return;
      let choice = 'auto';
      const heights = () => [...new Set([...Array(levels.length).keys()].map((i) => levels[i].height).filter(Boolean))].sort((a, b) => b - a);
      const currentHeight = () => levels[levels.selectedIndex]?.height;
      const render = () => {
        const list = heights();
        wrap.hidden = list.length < 2;
        const now = currentHeight();
        const current = choice === 'auto' ? (now ? `${autoLabel} · ${now}p` : autoLabel) : `${choice}p`;
        toggle.setAttribute('aria-label', `${toggle.dataset.label || (toggle.dataset.label = toggle.getAttribute('aria-label'))}: ${current}`);
        menu.innerHTML = ['auto', ...list].map((value) => `<li role="none"><button type="button" role="menuitemradio" aria-checked="${String(value) === String(choice)}" data-cvp-quality-option="${value}">${value === 'auto' ? (choice === 'auto' && now ? `${autoLabel} · ${now}p` : autoLabel) : `${value}p`}</button></li>`).join('');
      };
      const apply = () => {
        for (let i = 0; i < levels.length; i += 1) levels[i].enabled = choice === 'auto' || levels[i].height === choice;
        render();
      };
      levels.on('addqualitylevel', render);
      levels.on('change', render);
      const setMenu = (open) => {
        menu.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      };
      toggle.addEventListener('click', () => setMenu(menu.hidden));
      menu.addEventListener('click', (event) => {
        const option = event.target.closest('[data-cvp-quality-option]');
        if (!option) return;
        setMenu(false);
        choice = option.dataset.cvpQualityOption === 'auto' ? 'auto' : Number(option.dataset.cvpQualityOption);
        apply();
      });
    },

    // Full screen: the whole layer where supported; iPhone Safari only allows its native video player.
    toggleFullscreen() {
      const layer = this.layer;
      if (!layer) return;
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        return;
      }
      const request = layer.requestFullscreen || layer.webkitRequestFullscreen;
      if (request) {
        Promise.resolve(request.call(layer)).catch(() => {});
        return;
      }
      const video = layer.querySelector('video');
      if (video?.webkitEnterFullscreen) video.webkitEnterFullscreen();
    },

    close() {
      if (!this.host) return;
      const { host, layer, trigger } = this;
      if (document.fullscreenElement === layer) document.exitFullscreen().catch(() => {});
      else if (document.webkitFullscreenElement === layer) document.webkitExitFullscreen();
      this.request = (this.request || 0) + 1;
      this.observer?.disconnect();
      if (this.player && !this.player.isDisposed()) this.player.dispose();
      this.player = null;
      layer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.load();
      });
      layer.remove();
      host.classList.remove('is-playing-video');
      host.dispatchEvent(new CustomEvent('cyril:video-close', { bubbles: true }));
      this.host = this.layer = this.trigger = null;
      trigger?.focus({ preventScroll: true });
    },
  };

  // Modal: one shared dialog per page (snippets/cyril-video-modal.liquid).
  class CyrilVideoModal extends HTMLElement {
    connectedCallback() {
      // Live directly under <body> so no section stacking context or header can cover it.
      if (this.parentElement !== document.body) {
        document.body.append(this);
        return;
      }
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('.cvp__dialog');
      this.player = new CyrilPlayer(this.querySelector('[data-cvp-frame]'), optionsFrom(this));
      this.querySelectorAll('[data-cvp-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    open(trigger) {
      const sources = readSources(trigger);
      if (!sources.length) return;
      this.opener = trigger;
      this.hidden = false;
      document.documentElement.classList.add('cvp-locked');
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.dialog.focus({ preventScroll: true });
      this.player.play(sources);
    }

    close() {
      if (this.hidden) return;
      this.player.dispose();
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cvp-locked');
      setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 260);
      this.opener?.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = [...this.dialog.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])')]
        .filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('cyril-video-modal', CyrilVideoModal);
  window.CyrilPlayer = CyrilPlayer;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cyril-video]');
    if (!trigger) return;
    const host = trigger.hasAttribute('data-cyril-video-inline') && trigger.closest('[data-cvp-inline-host]');
    const modal = document.querySelector('cyril-video-modal');
    if (!host && !modal) return;
    event.preventDefault();
    event.stopPropagation();
    if (host) inline.open(trigger, host);
    else modal.open(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && inline.host && inline.host.contains(document.activeElement)) inline.close();
  });
})();
